<script setup>
import { computed, onBeforeMount, onMounted, ref } from "vue";
import { useLocalStorage } from "@vueuse/core";
import { onBeforeRouteUpdate, useRoute, useRouter } from "vue-router";
import { capitalize } from "lodash-es";
import EnrollmentHeader from "@/features/enrollment/EnrollmentHeader.vue";
import PhoneService from "@/api/actions/phone-service.js";
import EmailService from "@/api/actions/email-service.js";
import DataDeleteService from "@/api/actions/data-delete-service.js";
import { formatPhoneStringBasic } from "@/scripts/format.js";
import { posthogCapture } from "@/scripts/posthog.js";
import { useToast } from "@/composables/useToast.js";
import { useColorScheme } from "@/composables/useColorScheme.js";
import { useDataDeleteSessionStorage } from "@/features/data-delete/composables/useDataDeleteSessionStorage.js";
import { useDataDeleteOverlay } from "@/routes/DataDeletion/composables/useDataDeleteOverlay.js";
import { PH_EVENT_USER_SUBMITTED_DD_SUBMISSION_FORM } from "@/scripts/posthogEvents.js";

const route = useRoute();
const router = useRouter();

const request = useLocalStorage("enrollment-request", {
  name: {},
  dob: null,
  addresses: [],
  email_addresses: [],
  phone_numbers: [],
  agreesWithTerms: false,
  wasAutofilled: false,
});

const {
  readSearchProgressFromSessionStorage,
  clearSearchProgressFromSessionStorage,
} = useDataDeleteSessionStorage();

const autofillSearchResults = () => {
  const local = readSearchProgressFromSessionStorage();

  if (!local) {
    return;
  }

  const { year, month, day } = local?.DOB || {};

  request.value.name.first = capitalize(local?.firstName) || null;
  request.value.name.middle = capitalize(local?.middleName) || null;
  request.value.name.last = capitalize(local?.lastName) || null;
  request.value.dob = year && month && day ? `${month}-${day}-${year}` : null;
  request.value.email_addresses = local?.emails || [];
  request.value.phone_numbers = formatPhoneStringBasic(local?.phone)
    ? [formatPhoneStringBasic(local?.phone)]
    : [];
};

const autofillUserContacts = async () => {
  let userPhones = [];
  let userEmails = [];

  try {
    const [userPhonesData, userEmailsData] = await Promise.all([
      PhoneService.getUserPhoneNumbers(),
      EmailService.getUserEmails(),
    ]);

    userPhones =
      userPhonesData?.data?.results?.map((phone) =>
        formatPhoneStringBasic(phone.phone_number)
      ) ?? [];

    userEmails =
      userEmailsData?.data?.results?.map((email) => email.email) ?? [];
  } catch (error) {
    console.error(error);
  }

  request.value.phone_numbers = [...request.value.phone_numbers, ...userPhones];
  request.value.email_addresses = [
    ...request.value.email_addresses,
    ...userEmails,
  ];
};

// TODO: add feature flag
const autofillRequestOnce = async () => {
  if (request.value.wasAutofilled) {
    return;
  }

  autofillSearchResults();
  clearSearchProgressFromSessionStorage();
  autofillUserContacts();

  request.value.wasAutofilled = true;
};

onBeforeMount(autofillRequestOnce);

const steps = computed(() => [
  { route: "EnrollmentNames", title: "Set up Data Removal" },
  { route: "EnrollmentContacts", title: "Set up Data Removal" },
  { route: "EnrollmentAddresses", title: "Set up Data Removal" },
  { route: "EnrollmentConfirm", title: "Confirm Your Information" },
]);

const onNext = () => {
  const isCurrentStepValid = form.value.validateForm?.() ?? true;
  const nextStep = steps.value[route.meta.step];

  if (isCurrentStepValid && nextStep) {
    router.push({ name: nextStep.route });
  }
};

const onBack = () => {
  const nextStep = steps.value[route.meta.step - 2];

  if (nextStep) {
    router.push({ name: nextStep.route });
  }
};

const transition = ref("slide-left");

onBeforeRouteUpdate((to, from, next) => {
  const toStep = to.meta.step;
  const fromStep = from.meta.step;

  transition.value = toStep > fromStep ? "slide-left" : "slide-right";
  setTimeout(() => setColorScheme(to.meta.scheme), 200);

  next();
});

const form = ref(null);

const isSubmitting = ref(false);

const toast = useToast();

const onSubmitRemoval = async () => {
  try {
    isSubmitting.value = true;

    await DataDeleteService.createEnrollmentData({
      first_name: request?.value?.name?.first || undefined,
      last_name: request?.value?.name?.last || undefined,
      city: request?.value?.addresses?.[0]?.city || undefined,
      state: request?.value?.addresses?.[0]?.state || undefined,
      name: {
        first: request?.value?.name?.first || undefined,
        last: request?.value?.name?.last || undefined,
        middle: request?.value?.name?.middle || undefined,
        prefix: request?.value?.name?.prefix || undefined,
        suffix: request?.value?.name?.suffix || undefined,
      },
      other_names: [],
      birth_year: request?.value?.dob?.split("-").pop() || undefined,
      email_addresses: request?.value?.email_addresses || [],
      phone_numbers: request.value.phone_numbers.map(formatPhoneStringBasic),
      addresses: request.value.addresses || [],
    });

    posthogCapture(PH_EVENT_USER_SUBMITTED_DD_SUBMISSION_FORM);

    await onViewExposureStatus();
  } catch (error) {
    console.error(error);
    toast.error("Error creating your data deletion profile. Please try again.");
  } finally {
    isSubmitting.value = false;
  }
};

const { closeDataDeleteOverlay } = useDataDeleteOverlay();

const onViewExposureStatus = async () => {
  await closeDataDeleteOverlay();
  request.value = null;
  router.push({ name: "SummaryBasicMode" });
};

const { colorScheme, setColorScheme } = useColorScheme();
const enterScheme = ref(null);

onMounted(() => {
  enterScheme.value = colorScheme.value;
  setColorScheme(route.meta.scheme);
});
</script>

<template>
  <div
    class="page-enrollment"
    :class="`page-enrollment--enter-scheme-${enterScheme}`"
  >
    <div class="page-enrollment__content">
      <Transition
        :name="transition"
        mode="out-in"
      >
        <EnrollmentHeader
          v-if="$route.name !== 'EnrollmentReady'"
          :step="$route.meta.step"
          :steps="steps.map((step) => step.title)"
          @back="onBack"
        />
        <!-- don't remove, it triggers the transition -->
        <div v-else />
      </Transition>
      <router-view>
        <template #default="{ Component }">
          <Transition
            :name="transition"
            mode="out-in"
          >
            <Component
              :is="Component"
              ref="form"
              v-model:first-name="request.name.first"
              v-model:middle-name="request.name.middle"
              v-model:last-name="request.name.last"
              v-model:date-of-birth="request.dob"
              v-model:phone-numbers="request.phone_numbers"
              v-model:email-addresses="request.email_addresses"
              v-model:addresses="request.addresses"
              v-model:agrees-with-terms="request.agreesWithTerms"
              :is-submitting="isSubmitting"
              class="page-enrollment__card"
              @next="onNext"
              @submit-removal="onSubmitRemoval"
            />
          </Transition>
        </template>
      </router-view>
    </div>
  </div>
  <div class="page-enrollment__background" />
</template>

<style scoped lang="scss">
.page-enrollment {
  position: fixed;
  inset: 0;
  z-index: 1000;

  &__background {
    position: fixed;
    inset: 0;
    z-index: 998;
    background-color: $color-base;
    transition: background-color 0.5s ease;
  }

  &__content {
    width: 100%;
    padding: 20px 16px;
    opacity: 0;
    animation: appear-bottom-10 0.5s forwards;

    @media screen and (min-width: $screen-lg) {
      margin: clamp(32px, 50vh - 350px, 300px) auto 0;
      max-width: 440px;
    }

    @at-root .page-enrollment--enter-scheme-dark & {
      animation-delay: 0.3s;
    }
  }

  &__card {
    margin-top: 24px;

    @media screen and (min-width: $screen-lg) {
      margin-top: 32px;
    }
  }
}

.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.2s ease-in-out;
}

.slide-left-leave-to,
.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-15px);
}

.slide-left-enter-from,
.slide-right-leave-to {
  opacity: 0;
  transform: translateX(15px);
}
</style>
